import {
  Add,
  Delete,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { IvrDtmfRule, IvrDtmfStatus } from '../utils/reverseIvrDtmf';
import { createIvrRule, getIvrDtmfValidationErrorKey, IVR_DTMF_STATUSES } from '../utils/reverseIvrDtmf';

interface ReverseIvrDtmfEditorProps {
  rules: IvrDtmfRule[];
  onChange: (rules: IvrDtmfRule[]) => void;
  required?: boolean;
}

export default function ReverseIvrDtmfEditor({
  rules,
  onChange,
  required = false,
}: ReverseIvrDtmfEditorProps) {
  const { t } = useTranslation(['pages']);
  const validationErrorKey = getIvrDtmfValidationErrorKey(rules);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">
          {t('pages:campaigns.create.basicInfo.ivrDtmfMap')}
          {required ? ' *' : ''}
        </Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => onChange([...rules, createIvrRule()])}
          disabled={rules.length >= 10}
        >
          {t('pages:campaigns.create.basicInfo.ivrDtmfAddRule')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rules.map((rule) => (
          <Box key={rule.id} sx={{ display: 'grid', gap: 1, gridTemplateColumns: '120px 1fr 1fr auto' }}>
            <FormControl size="small">
              <InputLabel>{t('pages:campaigns.create.basicInfo.ivrDtmfDigit')}</InputLabel>
              <Select
                value={rule.digit}
                label={t('pages:campaigns.create.basicInfo.ivrDtmfDigit')}
                onChange={(event) => {
                  onChange(rules.map((item) => (
                    item.id === rule.id
                      ? { ...item, digit: String(event.target.value) }
                      : item
                  )));
                }}
              >
                {Array.from({ length: 10 }).map((_, digit) => (
                  <MenuItem key={digit} value={String(digit)}>
                    {digit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t('pages:campaigns.create.basicInfo.ivrDtmfStatus')}</InputLabel>
              <Select
                value={rule.status}
                label={t('pages:campaigns.create.basicInfo.ivrDtmfStatus')}
                onChange={(event) => {
                  onChange(rules.map((item) => (
                    item.id === rule.id
                      ? { ...item, status: event.target.value as IvrDtmfStatus }
                      : item
                  )));
                }}
              >
                {IVR_DTMF_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {t(`pages:campaigns.create.basicInfo.ivrDtmfStatusValues.${status}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label={t('pages:campaigns.create.basicInfo.ivrDtmfLabel')}
              value={rule.label}
              onChange={(event) => {
                onChange(rules.map((item) => (
                  item.id === rule.id
                    ? { ...item, label: event.target.value }
                    : item
                )));
              }}
            />
            <IconButton
              color="error"
              onClick={() => {
                if (rules.length <= 1) {
                  return;
                }
                onChange(rules.filter((item) => item.id !== rule.id));
              }}
              disabled={rules.length <= 1}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
      </Box>

      {validationErrorKey && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t(validationErrorKey)}
        </Alert>
      )}
    </Paper>
  );
}
